/**
 * Authentication Context
 * 
 * Provides shared authentication state across all components.
 * Wraps the useAuth logic in a React Context for proper state sharing.
 * 
 * @module contexts/AuthContext
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { AuthState, AuthUser, IAuthService } from '@/types/auth';
import { MockAuthService } from '@/services/auth/MockAuthService';
import { TokenPasteAuthService } from '@/services/auth/TokenPasteAuthService';
import { GCIPAuthService } from '@/services/auth/GCIPAuthService';
import { decodeJWT, isTokenExpired } from '@/utils/jwt';
import { clearCookiesForCurrentOrigin } from '@/utils/cookies';
import { clearSiteStorageOnLogout } from '@/utils/clearSiteStorage';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { MfeLoader } from '@/services/mfe/MfeLoader';

/** Fired when an API returns 401 so the shell can logout and redirect to login */
export const SESSION_EXPIRED_EVENT = 'LORE_SESSION_EXPIRED';

/** Interval (ms) to re-check token expiry while user is logged in */
const TOKEN_EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Get authentication service based on environment
 */
function getAuthService(): IAuthService {
  const authMode = process.env.REACT_APP_AUTH_MODE || 'mock';
  switch (authMode) {
    case 'mock':
      return new MockAuthService();
    case 'token-paste':
      return new TokenPasteAuthService();
    case 'gcip':
      return new GCIPAuthService();
    default:
      return new MockAuthService();
  }
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const authService = useMemo(() => getAuthService(), []);
  const isInitializing = React.useRef(false);

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    error: null,
  });

  // Initialize auth state on mount
  useEffect(() => {
    // Prevent multiple simultaneous initializations (React StrictMode)
    if (isInitializing.current) {
      return;
    }
    isInitializing.current = true;
    const initAuth = async () => {
      try {
        const token = await authService.getAccessToken();
        if (!token) {
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const claims = decodeJWT(token);
        if (!claims) {
          await authService.logout();
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        if (isTokenExpired(token)) {
          await authService.logout();
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const user: AuthUser = {
          id: claims.ref_user_id,
          email: claims.email,
        };

        // Set token in CommunicationBridge BEFORE updating state
        // This ensures the token is available when ProfileContext fetches
        CommunicationBridge.setAccessToken(token);

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user,
          token,
          error: null,
        });

      } catch (error) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Auth initialization failed',
        }));
      } finally {
        isInitializing.current = false;
      }
    };

    initAuth();
  }, [authService]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      // Do not set isLoading: true here — it would unmount the sign-in form and clear
      // email/password state. The form uses its own isSubmitting for button feedback.
      setAuthState(prev => ({ ...prev, error: null }));

      const { accessToken } = await authService.login(email, password);

      const claims = decodeJWT(accessToken);
      if (!claims) {
        throw new Error('Failed to decode token');
      }

      const user: AuthUser = {
        id: claims.ref_user_id,
        email: claims.email,
      };

      // Set token in CommunicationBridge BEFORE updating state
      CommunicationBridge.setAccessToken(accessToken);

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user,
        token: accessToken,
        error: null,
      });

    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      throw error;
    }
  }, [authService]);

  const loginWithToken = useCallback(async (token: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      const { accessToken } = await authService.loginWithToken(token);

      const claims = decodeJWT(accessToken);
      if (!claims) {
        throw new Error('Failed to decode token');
      }

      const user: AuthUser = {
        id: claims.ref_user_id,
        email: claims.email,
      };

      // Set token in CommunicationBridge BEFORE updating state
      CommunicationBridge.setAccessToken(accessToken);

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user,
        token: accessToken,
        error: null,
      });

    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Token login failed',
      }));
      throw error;
    }
  }, [authService]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null,
      });
      CommunicationBridge.setAccessToken('');
      MfeLoader.clearCacheAndContainers();
      clearCookiesForCurrentOrigin();
      clearSiteStorageOnLogout();
    } catch (error) {
      // Still clear local state even if service logout fails
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null,
      });
      MfeLoader.clearCacheAndContainers();
      clearCookiesForCurrentOrigin();
      clearSiteStorageOnLogout();
    }
  }, [authService]);

  // When any API returns 401, logout and redirect to login
  const logoutRef = React.useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    const handleSessionExpired = () => {
      logoutRef.current?.();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  // Periodic token expiry check so we logout before the app gets into a weird state
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) return;
    const checkExpiry = () => {
      if (isTokenExpired(authState.token!)) {
        logout();
      }
    };
    const id = setInterval(checkExpiry, TOKEN_EXPIRY_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [authState.isAuthenticated, authState.token, logout]);

  const value: AuthContextType = {
    ...authState,
    login,
    loginWithToken,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

